import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { cn } from '@/lib/utils'
import {
  createEventParticipant,
  updateEventParticipantStatus,
} from 'app/frontend/base/api/event'
import { useContrastedBackground } from 'app/frontend/base/components/Card'
import EventStatusIcon from 'app/frontend/base/components/EventStatusIcon'
import { useState } from 'react'
import {
  EventOverviewStatusByEventValue,
  useUserStat,
  type UserStatView,
} from '../utils/useUserStat'
import { useAuth } from 'app/frontend/base/config/context/auth'
import { usePageInfo } from 'app/frontend/base/config/queries'
import { eventParticipantStatuses } from 'app/frontend/types/custom/EventParticipantStatus'
import { useTeam } from 'app/frontend/base/config/context/team'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { displayErrorToast } from 'app/frontend/base/helpers'
import EventParticipantStatusItems from '../../TeamEvent/components/EventParticipantStatusItems'
import TeamMemberLink from 'app/frontend/base/components/TeamMemberLink'
import BlizzardIcon from 'app/frontend/base/components/BlizzardIcon'
import CharacterClassIcon from 'app/frontend/base/components/CharacterClassIcon'
import CharacterRoleIcon from 'app/frontend/base/components/CharacterRoleIcon'
import { getSpecialization } from 'app/frontend/base/services/characterValues'

export type EventOverviewUserProps = {
  main: wowaudit.TeamMembership
  visibleEvents: wowaudit.EventReference[]
  statusByEvent: Map<number, EventOverviewStatusByEventValue>
  statView: UserStatView
}

const EventOverviewUser = (props: EventOverviewUserProps) => {
  const { team } = useTeam()
  const { main, visibleEvents, statusByEvent } = props
  const {
    data: { user },
  } = usePageInfo()
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const [openDropdownEventId, setOpenDropdownEventId] = useState<number>()

  const background = useContrastedBackground()
  const { tooltipValue, displayValue } = useUserStat(props)
  const { accessLevel } = useAuth()

  const isOwnCharacter = user && user?.id === main.characterReference.userId
  const selectableStatuses = eventParticipantStatuses.filter(
    (nextStatus) =>
      nextStatus !== 'removed' &&
      (accessLevel === 'admin' ||
        team.settings.raidsAvailableStatuses.value.includes(nextStatus)),
  )
  const { mutate: updateStatus, isPending: isUpdatingStatus } = useMutation({
    mutationFn: ({
      eventId,
      eventParticipantId,
      nextStatus,
    }: {
      eventId: number
      eventParticipantId?: number
      nextStatus: wowaudit.EventParticipantReference['status']
    }) => {
      if (eventParticipantId) {
        return updateEventParticipantStatus(
          team.id,
          eventParticipantId,
          nextStatus,
        )
      }
      return createEventParticipant(team.id, {
        eventId,
        characterId: main.characterReference.id,
        addedAsGuest: true,
        role: main.role,
        specId: main.specId ?? undefined,
        status: nextStatus,
      })
    },
    onSuccess: (updatedEvent) => {
      queryClient.setQueryData(
        ['teamEvents', { id: team.id }],
        (existingEvents: wowaudit.EventReference[] | undefined) => {
          if (!existingEvents) return existingEvents
          return existingEvents.map((event) =>
            event.id === updatedEvent.id ? updatedEvent : event,
          )
        },
      )
      queryClient.invalidateQueries({
        predicate: (query) => {
          const [key, args] = query.queryKey as [
            string,
            { id?: number } | undefined,
          ]
          return key === 'teamOverviewEvents' && args?.id === team.id
        },
      })

      toast.success(t('dashboard.team.events.status-updated'))
      setOpenDropdownEventId(undefined)
    },
    onError: (error) => {
      displayErrorToast(error, t)
    },
  })

  return (
    <tr
      className={cn(
        '[&>td]:border-y [&>td]:py-1 [&>td:last-child]:border-r [&>td:last-child]:rounded-r-md',
        background,
      )}
    >
      <td className='border-l rounded-l-md pl-1'>
        <TeamMemberLink member={main} iconType='both' />
      </td>

      <td className='px-2'>
        <Tooltip>
          <TooltipContent>{tooltipValue}</TooltipContent>
          <TooltipTrigger className='w-full text-xs font-normal text-right'>
            {displayValue}
          </TooltipTrigger>
        </Tooltip>
      </td>

      {visibleEvents.map((event) => {
        const eventStatus = statusByEvent.get(event.id)
        const status = eventStatus?.status ?? 'unknown'
        const participantId = eventStatus?.participantId
        const participant = participantId
          ? event.participantReferences.find(
              (eventParticipant) => eventParticipant.id === participantId,
            )
          : undefined
        const participantDisplayCharacter =
          participant?.altCharacterReference ?? participant?.characterReference
        const participantDisplayMeta =
          participant && participantDisplayCharacter
            ? {
                id: participantDisplayCharacter.id,
                name: participantDisplayCharacter.name,
                characterClass: participantDisplayCharacter.characterClass,
                role: participant.role,
                specName:
                  participant.specId == null
                    ? undefined
                    : getSpecialization(participant.specId.toString())?.name,
                specIcon:
                  participant.specId == null
                    ? undefined
                    : getSpecialization(participant.specId.toString())?.icon,
              }
            : undefined
        const usesDifferentCharacter =
          !!participantDisplayMeta &&
          participantDisplayMeta.id !== main.characterReference.id
        const hasSpecOverride =
          participant?.specId != null && participant.specId !== main.specId
        const statusIcon = <EventStatusIcon status={status} size='small' />
        const showParticipantIcon =
          !!participantDisplayMeta &&
          (usesDifferentCharacter || (isOwnCharacter && hasSpecOverride))
        const showSpecIcon =
          isOwnCharacter && hasSpecOverride && !usesDifferentCharacter
        const iconGroup =
          showParticipantIcon && participantDisplayMeta ? (
            <div className='flex items-center justify-center gap-1'>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className='inline-flex mr-1'>
                    {showSpecIcon && participantDisplayMeta.specIcon ? (
                      <BlizzardIcon
                        iconName={participantDisplayMeta.specIcon}
                        size='medium'
                      />
                    ) : (
                      <CharacterClassIcon
                        size='medium'
                        characterClass={participantDisplayMeta.characterClass}
                      />
                    )}
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <div className='flex items-center gap-1.5 justify-center'>
                    <CharacterRoleIcon
                      role={participantDisplayMeta.role}
                      size='small'
                    />
                    <span>{participantDisplayMeta.name}</span>
                  </div>
                  <div className='text-xs text-muted-foreground text-center'>
                    {participantDisplayMeta.specName
                      ? `${participantDisplayMeta.specName} ${participantDisplayMeta.characterClass}`
                      : participantDisplayMeta.characterClass}
                  </div>
                </TooltipContent>
              </Tooltip>
              {statusIcon}
            </div>
          ) : (
            statusIcon
          )

        const editable =
          accessLevel === 'admin' ||
          (isOwnCharacter && event.status !== 'locked')
        const canEditParticipant = editable
        return (
          <td
            key={event.id}
            className={cn(
              'border-l relative',
              canEditParticipant && 'hover:bg-card transition-colors',
              event.optional ? 'bg-info' : '',
              event.status === 'cancelled' ? 'bg-destructive' : '',
            )}
          >
            {canEditParticipant ? (
              <DropdownMenu
                open={openDropdownEventId === event.id}
                onOpenChange={(open) =>
                  setOpenDropdownEventId(open ? event.id : undefined)
                }
              >
                <DropdownMenuTrigger
                  className='focus:outline-none focus:bg-card absolute inset-0 flex items-center justify-center'
                  disabled={isUpdatingStatus}
                >
                  {iconGroup}
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  className='w-fit p-1'
                  side='bottom'
                  align='center'
                >
                  <EventParticipantStatusItems
                    currentStatus={status}
                    statuses={selectableStatuses}
                    onSelect={(nextStatus) =>
                      updateStatus({
                        eventId: event.id,
                        eventParticipantId: participantId,
                        nextStatus,
                      })
                    }
                  />
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className='flex justify-center'>{iconGroup}</div>
            )}
          </td>
        )
      })}
    </tr>
  )
}

export default EventOverviewUser
